import { Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import {
  SonarQubeOptionPickerFieldExtension,
  PrincipalsEditorFieldExtension,
} from './components/scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  TechDocsReaderPage,
  techdocsPlugin,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import { TechDocsCustomLayout } from './components/docs/CustomTechDocsReaderPage';
import { FeedbackSidebarAddon, PrimarySidebarSpacer } from './components/docs/FeedbackSidebarAddon';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { customCatalogColumns, customDocsColumns } from './components/catalog/customCatalogTable';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import { UserSettings } from './components/settings/Settings';
import { RbacPage } from '@backstage-community/plugin-rbac/legacy';
import { CardFrontPage } from './components/home/CardFrontPage';
import {
  ProductTeamsPage,
  ProductTeamPage,
  ProductTeam,
  productTeamsApiRef,
} from '@internal/plugin-product-teams';
import { CreateDevBoxServiceGroupPage } from '@internal/plugin-devbox-react';
import {
  CreateSonarQubeProjectPage,
  CreateSonarQubeServiceGroupPage,
  SonarQubeServiceGroup,
  SonarQubeServiceGroupPage,
  UpdateSonarQubeProjectPage,
  UpdateSonarQubeServiceGroupPage,
} from '@internal/plugin-sonarqube-react';
import { ComponentType, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import useAsync from 'react-use/lib/useAsync';
import { BreadcrumbItem } from './types/header/breadcrumb';
import { pageHeaderOverrideStore } from './utils/pageHeaderOverride';

import { CustomSignInPage } from './components/auth/CustomSignInPage';
import { SignInPage as GuestSignInPage } from '@backstage/core-components';
import { configApiRef, SignInPageProps, useApi } from '@backstage/core-plugin-api';
import { createApp } from '@backstage/frontend-defaults';
import { convertLegacyAppOptions, convertLegacyAppRoot } from '@backstage/core-compat-api';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { UnifiedThemeProvider } from '@backstage/theme';
import { arlaTheme } from './themes/arlaBackstage';
import LightIcon from '@material-ui/icons/WbSunny';
import DarkIcon from '@material-ui/icons/Brightness4';
import { copyRoutableExtensionData } from './utils';
import './styles.css';

const AppSignInPage = (props: SignInPageProps) => {
  const guestMode = useApi(configApiRef).getOptionalBoolean('app.localDev.guestMode');
  return guestMode ? (
    <GuestSignInPage {...props} providers={['guest']} />
  ) : (
    <CustomSignInPage {...props} />
  );
};

const GatedCatalogImportPage = copyRoutableExtensionData(
  () => (
    <RequirePermission permission={catalogEntityCreatePermission}>
      <CatalogImportPage />
    </RequirePermission>
  ),
  CatalogImportPage,
);

// The plugin's team-detail pages fetch their own data (id-keyed, per the backend contract)
// and know the team's real display `name` once loaded — these wrappers pass that up to
// GlobalBreadcrumb via pageHeaderOverrideStore so the breadcrumb/title show the real name
// instead of the raw id in the URL (see utils/pageHeaderOverride.ts).
//
// Wrapping a routable-extension component like this normally breaks Backstage's route
// discovery ("Routable extension component ... was not discovered in the app element tree")
// since it inspects the Route's element type directly — copyRoutableExtensionData (already
// used above for GatedCatalogImportPage) copies the original's plugin/mountPoint tag onto
// the wrapper so discovery still finds it.
const useTeamPageHeaderOverride = (breadcrumbSuffix?: string) => {
  const location = useLocation();

  useEffect(() => {
    // Clear immediately on every path change too (not just on unmount): React Router
    // reuses this component instance across param changes on the same route pattern (e.g.
    // navigating from one team's page straight to another's), so without this the stale
    // previous team's title/breadcrumb would linger instead of showing the loading skeleton
    // (see pageConfig.ts) while the new team's data loads.
    pageHeaderOverrideStore.setOverride(null);
    return () => pageHeaderOverrideStore.setOverride(null);
  }, [location.pathname]);

  return useCallback(
    // `innerCrumbs` go between the team and the suffix, e.g. a service group.
    (team: ProductTeam, innerCrumbs: BreadcrumbItem[] = []) => {
      const breadcrumbs = [
        { label: 'Product Teams', path: '/product-teams' },
        breadcrumbSuffix
          ? { label: team.name, path: `/product-teams/${encodeURIComponent(team.id)}` }
          : { label: team.name },
        ...innerCrumbs,
        ...(breadcrumbSuffix ? [{ label: breadcrumbSuffix }] : []),
      ];
      pageHeaderOverrideStore.setOverride({
        title: breadcrumbSuffix ?? team.name,
        breadcrumbs,
      });
    },
    [breadcrumbSuffix],
  );
};

const ProductTeamPageWithHeader = copyRoutableExtensionData(
  () => {
    const onTeamLoaded = useTeamPageHeaderOverride();
    return <ProductTeamPage onTeamLoaded={onTeamLoaded} />;
  },
  ProductTeamPage,
);

// The tool libraries can't fetch teams, so the team is fetched here and passed in.
const withTeamHeader = (
  title: string,
  Page: ComponentType<{ team?: ProductTeam }>,
) => () => {
  const { id = '' } = useParams();
  const productTeamsApi = useApi(productTeamsApiRef);
  const setHeader = useTeamPageHeaderOverride(title);
  const { value: team } = useAsync(
    () => productTeamsApi.getTeam(id),
    [productTeamsApi, id],
  );

  useEffect(() => {
    if (team) {
      setHeader(team);
    }
  }, [team, setHeader]);

  return <Page team={team} />;
};

const CreateSonarQubeServiceGroupPageWithHeader = withTeamHeader(
  'Create new group',
  CreateSonarQubeServiceGroupPage,
);
const CreateDevBoxServiceGroupPageWithHeader = withTeamHeader(
  'Create new group',
  CreateDevBoxServiceGroupPage,
);

// Not a routable extension (sonarqube-react is a library), so no copyRoutableExtensionData. The
// page only knows the service group; the team for the breadcrumb is fetched here.
const withServiceGroupHeader = (
  title: string,
  Page: ComponentType<{
    onServiceGroupLoaded?: (serviceGroup: SonarQubeServiceGroup) => void;
  }>,
) => () => {
  const { id = '' } = useParams();
  const productTeamsApi = useApi(productTeamsApiRef);
  const setHeader = useTeamPageHeaderOverride(title);
  const { value: team } = useAsync(
    () => productTeamsApi.getTeam(id),
    [productTeamsApi, id],
  );
  const [serviceGroup, setServiceGroup] = useState<SonarQubeServiceGroup>();

  useEffect(() => {
    if (team && serviceGroup) {
      setHeader(team, [
        {
          label: serviceGroup.applicationName,
          path: `/product-teams/${encodeURIComponent(id)}/tooling-groups/sonarqube/${encodeURIComponent(serviceGroup.id)}`,
        },
      ]);
    }
  }, [team, serviceGroup, setHeader, id]);

  return <Page onServiceGroupLoaded={setServiceGroup} />;
};

const CreateSonarQubeProjectPageWithHeader = withServiceGroupHeader(
  'Create SonarQube Project',
  CreateSonarQubeProjectPage,
);
const UpdateSonarQubeProjectPageWithHeader = withServiceGroupHeader(
  'Update SonarQube Project',
  UpdateSonarQubeProjectPage,
);
const UpdateSonarQubeServiceGroupPageWithHeader = withServiceGroupHeader(
  'Update tooling group',
  UpdateSonarQubeServiceGroupPage,
);

// Like withServiceGroupHeader; the service group's name is the page title.
const SonarQubeServiceGroupPageWithHeader = () => {
  const { id = '' } = useParams();
  const productTeamsApi = useApi(productTeamsApiRef);
  const [serviceGroup, setServiceGroup] = useState<SonarQubeServiceGroup>();
  const setHeader = useTeamPageHeaderOverride(serviceGroup?.applicationName);
  const { value: team } = useAsync(
    () => productTeamsApi.getTeam(id),
    [productTeamsApi, id],
  );

  useEffect(() => {
    if (team && serviceGroup) {
      setHeader(team);
    }
  }, [team, serviceGroup, setHeader]);

  const teamMemberEntraIds = useMemo(
    () => team?.members.flatMap(m => (m.entraId ? [m.entraId] : [])),
    [team],
  );

  return (
    <SonarQubeServiceGroupPage
      onServiceGroupLoaded={setServiceGroup}
      teamMemberEntraIds={teamMemberEntraIds}
    />
  );
};

const routes = (
  <FlatRoutes>
    <Route path="/" element={<CardFrontPage />} />
    <Route path="/catalog" element={<CatalogIndexPage columns={customCatalogColumns} />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage columns={customDocsColumns} />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={
        <TechDocsReaderPage>
          <TechDocsCustomLayout />
        </TechDocsReaderPage>
      }
    >
      <TechDocsAddons>
        <ReportIssue />
        <Mermaid />
        <FeedbackSidebarAddon />
        <PrimarySidebarSpacer />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />}>
      <ScaffolderFieldExtensions>
        <SonarQubeOptionPickerFieldExtension />
        <PrincipalsEditorFieldExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route path="/catalog-import" element={<GatedCatalogImportPage />} />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/product-teams" element={<ProductTeamsPage />} />
    <Route path="/product-teams/:id" element={<ProductTeamPageWithHeader />} />
    <Route
      path="/product-teams/:id/tooling-groups/devbox/create"
      element={<CreateDevBoxServiceGroupPageWithHeader />}
    />
    <Route
      path="/product-teams/:id/tooling-groups/sonarqube/create"
      element={<CreateSonarQubeServiceGroupPageWithHeader />}
    />
    <Route
      path="/product-teams/:id/tooling-groups/sonarqube/:serviceGroupId"
      element={<SonarQubeServiceGroupPageWithHeader />}
    />
    <Route
      path="/product-teams/:id/tooling-groups/sonarqube/:serviceGroupId/edit"
      element={<UpdateSonarQubeServiceGroupPageWithHeader />}
    />
    <Route
      path="/product-teams/:id/tooling-groups/sonarqube/:serviceGroupId/projects/create"
      element={<CreateSonarQubeProjectPageWithHeader />}
    />
    <Route
      path="/product-teams/:id/tooling-groups/sonarqube/:serviceGroupId/projects/:projectKey/edit"
      element={<UpdateSonarQubeProjectPageWithHeader />}
    />
    <Route path="/settings/*" element={<UserSettings />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/rbac" element={<RbacPage />} />
  </FlatRoutes>
);

const app = createApp({
  features: [
    convertLegacyAppOptions({
      apis,
      themes: [
        {
          id: 'arla-light',
          title: 'Light mode',
          variant: 'light',
          icon: <LightIcon />,
          Provider: ({ children }) => (
            <UnifiedThemeProvider theme={arlaTheme('light')} children={children} />
          ),
        },
        {
          id: 'arla-dark',
          title: 'Dark mode',
          variant: 'dark',
          icon: <DarkIcon />,
          Provider: ({ children }) => (
            <UnifiedThemeProvider theme={arlaTheme('dark')} children={children} />
          ),
        },
      ],
      components: {
        // `yarn start` runs with no backend available — skip real auth and
        // go straight in as a guest so the frontend can be developed without
        // backstage-auth-identity/backstage-catalog running. The flag is only
        // set in local-dev-server-only.yaml, so `yarn start:compose` and
        // production/docker builds use the real sign-in page.
        SignInPage: props => <AppSignInPage {...props} />,
      },
    }),
    ...convertLegacyAppRoot(
      <AppRouter>
        <Root>{routes}</Root>
      </AppRouter>,
      { entityPage },
    ),
  ],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
});

export default app.createRoot();
